import { BreadcrumComponent } from '../breadcrum/breadcrum.component';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiService } from '../../shared/services/api.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { LoaderService } from '../../shared/services/loader.service';
import {
  SCRAP_CERT_NAME_PATTERN,
  SCRAP_CERT_NUMBER_PATTERN,
  NAVIGATION_KEYS,
} from '../../shared/constants/scrap-certificate-validation';

@Component({
  selector: 'app-scrap-certificate',
  standalone: true,
  templateUrl: './scrap-certificate.component.html',
  styleUrls: ['./scrap-certificate.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, FormsModule, BreadcrumComponent],
})
export class ScrapCertificateComponent implements OnInit, OnDestroy {
  @Input() isStandalone: boolean = true;
  scrapCertificateBreadcrumbs: any[] = [{ label: 'Scrap Certificate', url: null }];
  // Form validation
  certificateForm!: FormGroup;
  isFormSubmitted = false;

  ImageAray: any[] = [];
  fileValidationErrors: string[] = [];

  certificates: any[] = [];
  scrapCategories: any;
  scrapList: any;
  cert: any;
  listdata: any;
  userData: any;
  filteredCategory: string | null = null;

  // Date picker properties
  showDatePicker: boolean = false;
  isMobile: boolean = false;
  currentCalendarDate: Date = new Date();
  selectedDateValue: Date | null = null;
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth();
  showMonthYearPicker: boolean = false;
  calendarDays: any[] = [];
  dayNames: string[] = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  monthNames: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  availableYears: number[] = [];
  currentDateField: 'issueDate' | 'expiryDate' | null = null;

  // Category bottom sheet properties
  showCategoryBottomSheet: boolean = false;
  selectedCategoryValue: string = '';

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    private apiService: ApiService,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initializeForm();
  }

  statusIcons: Record<string, SafeHtml> = {};

  initializeForm() {
    this.certificateForm = this.fb.group(
      {
        scrapCategory: ['', Validators.required],
        certificateName: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NAME_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        certificateNumber: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NUMBER_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        issuingAuthority: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NAME_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        companyName: [
          '',
          [
            Validators.required,
            Validators.pattern(SCRAP_CERT_NAME_PATTERN),
            this.noLeadingSpaceValidator.bind(this),
          ],
        ],
        issueDate: ['', Validators.required],
        expiryDate: ['', Validators.required],
      },
      { validators: this.dateValidator },
    );
  }

  // Custom validator to reject leading spaces
  noLeadingSpaceValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value && typeof value === 'string') {
      if (value.startsWith(' ')) {
        return { leadingSpace: true };
      }
    }
    return null;
  }

  // Custom validator to ensure expiry date is greater than issue date
  dateValidator(control: AbstractControl): ValidationErrors | null {
    const issueDate = control.get('issueDate')?.value;
    const expiryDate = control.get('expiryDate')?.value;

    if (!issueDate || !expiryDate) {
      return null; // Don't validate if either date is missing
    }

    const issue = new Date(issueDate);
    const expiry = new Date(expiryDate);

    if (expiry <= issue) {
      return { dateInvalid: true };
    }

    return null;
  }

  initializeDatePicker() {
    // Generate available years (current year ± 50 years) - limited to 4-digit years
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 50; i <= currentYear + 50; i++) {
      // Ensure only 4-digit years are added
      if (i >= 1000 && i <= 9999) {
        this.availableYears.push(i);
      }
    }
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
  }

  openDatePicker(fieldName: 'issueDate' | 'expiryDate', event?: Event) {
    // Only show custom picker on mobile
    if (!this.isMobile) {
      return; // Let native date picker handle desktop
    }

    if (event) {
      event.preventDefault();
    }

    this.currentDateField = fieldName;

    // Initialize with current form value or today
    const formDate = this.certificateForm.get(fieldName)?.value;
    if (formDate) {
      const date = new Date(formDate);
      if (!isNaN(date.getTime())) {
        this.currentCalendarDate = new Date(date);
        this.selectedDateValue = new Date(date);
        this.selectedYear = date.getFullYear();
        this.selectedMonth = date.getMonth();
      }
    } else {
      this.currentCalendarDate = new Date();
      this.selectedYear = this.currentCalendarDate.getFullYear();
      this.selectedMonth = this.currentCalendarDate.getMonth();
      this.selectedDateValue = null;
    }

    this.showMonthYearPicker = false;
    this.generateCalendar();
    this.showDatePicker = true;
    this.disableBodyScroll();
  }

  closeDatePicker() {
    this.showDatePicker = false;
    this.showMonthYearPicker = false;
    this.currentDateField = null;
    this.enableBodyScroll();
  }

  openCategoryBottomSheet() {
    // Only show bottom sheet on mobile
    if (!this.isMobile) {
      return; // Let native select handle desktop
    }

    // Initialize with current form value
    const currentValue = this.certificateForm.get('scrapCategory')?.value || '';
    this.selectedCategoryValue = currentValue;
    this.showCategoryBottomSheet = true;
    this.disableBodyScroll();
  }

  closeCategoryBottomSheet() {
    this.showCategoryBottomSheet = false;
    this.enableBodyScroll();
  }

  selectCategory(categoryName: string) {
    this.selectedCategoryValue = categoryName;
  }

  confirmCategorySelection() {
    if (this.selectedCategoryValue) {
      this.certificateForm.patchValue({
        scrapCategory: this.selectedCategoryValue,
      });
      this.certificateForm.get('scrapCategory')?.markAsTouched();
    }
    this.closeCategoryBottomSheet();
  }

  getSelectedCategoryDisplay(): string {
    const selectedValue = this.certificateForm.get('scrapCategory')?.value;
    if (!selectedValue) {
      return 'Select any one Category';
    }
    return selectedValue;
  }

  getSelectedDateDisplay(fieldName: 'issueDate' | 'expiryDate'): string {
    const value = this.certificateForm.get(fieldName)?.value;
    if (!value) {
      return fieldName === 'issueDate' ? 'Issue date of certificate' : 'Expiry date of certificate';
    }
    const parts = value.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return value;
  }

  generateCalendar() {
    const year = this.currentCalendarDate.getFullYear();
    const month = this.currentCalendarDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0

    this.calendarDays = [];

    // Add previous month's trailing days
    if (startingDayOfWeek > 0) {
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        this.calendarDays.push({
          date: prevMonthLastDay - i,
          isOtherMonth: true,
          isSelected: false,
          isToday: false,
          isDisabled: false,
          fullDate: new Date(year, month - 1, prevMonthLastDay - i),
        });
      }
    }

    // Add current month's days
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      currentDate.setHours(0, 0, 0, 0);

      const isSelected =
        this.selectedDateValue &&
        currentDate.getTime() === this.selectedDateValue.getTime();
      const isToday = currentDate.getTime() === today.getTime();
      const isDisabled = false; // Allow all dates for certificate dates

      this.calendarDays.push({
        date: day,
        isOtherMonth: false,
        isSelected: isSelected,
        isToday: isToday,
        isDisabled: isDisabled,
        fullDate: currentDate,
      });
    }

    // Add next month's leading days to fill the grid
    const remainingDays = 42 - this.calendarDays.length; // 6 rows * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      this.calendarDays.push({
        date: day,
        isOtherMonth: true,
        isSelected: false,
        isToday: false,
        isDisabled: false,
        fullDate: new Date(year, month + 1, day),
      });
    }
  }

  previousMonth() {
    this.currentCalendarDate = new Date(
      this.currentCalendarDate.getFullYear(),
      this.currentCalendarDate.getMonth() - 1,
      1,
    );
    this.selectedYear = this.currentCalendarDate.getFullYear();
    this.selectedMonth = this.currentCalendarDate.getMonth();
    this.generateCalendar();
  }

  nextMonth() {
    this.currentCalendarDate = new Date(
      this.currentCalendarDate.getFullYear(),
      this.currentCalendarDate.getMonth() + 1,
      1,
    );
    this.selectedYear = this.currentCalendarDate.getFullYear();
    this.selectedMonth = this.currentCalendarDate.getMonth();
    this.generateCalendar();
  }

  toggleMonthYearPicker() {
    this.showMonthYearPicker = !this.showMonthYearPicker;
  }

  onYearChange() {
    this.currentCalendarDate = new Date(
      this.selectedYear,
      this.selectedMonth,
      1,
    );
    this.generateCalendar();
    this.showMonthYearPicker = false;
  }

  onMonthChange() {
    this.currentCalendarDate = new Date(
      this.selectedYear,
      this.selectedMonth,
      1,
    );
    this.generateCalendar();
    this.showMonthYearPicker = false;
  }

  selectDate(day: any) {
    if (day.isDisabled || day.isOtherMonth) return;

    this.selectedDateValue = new Date(day.fullDate);
    this.generateCalendar();
  }

  confirmDateSelection() {
    if (this.selectedDateValue && this.currentDateField) {
      const formattedDate = this.formatDateToYYYYMMDD(
        this.selectedDateValue.toISOString(),
      );
      this.certificateForm.patchValue({
        [this.currentDateField]: formattedDate,
      });
      this.certificateForm.get(this.currentDateField)?.markAsTouched();
      this.closeDatePicker();
    }
  }

  getCurrentMonthYear(): string {
    return `${this.monthNames[this.currentCalendarDate.getMonth()]} ${this.currentCalendarDate.getFullYear()}`;
  }

  formatDateToYYYYMMDD(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // File validation function
  private validateFile(file: File): { isValid: boolean; error?: string } {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'application/pdf',
    ];
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File "${file.name}" is not a valid format. Only JPG, JPEG, PNG, WEBP and PDF files are allowed.`,
      };
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File "${file.name}" is too large. Maximum file size is 5MB.`,
      };
    }
    return { isValid: true };
  }

  ngOnInit() {
    try {
      const userDataString = localStorage.getItem('userData1');
      if (userDataString) {
        if (userDataString.startsWith('U2FsdGVkX1')) {
          this.userData = {};
        } else {
          this.userData = JSON.parse(userDataString);
        }
      } else {
        this.userData = {};
      }
    } catch (error) {
      console.error('Error parsing userData1 from localStorage:', error);
      this.userData = {};
    }
    this.setupFormValidation();
    this.initializeDatePicker();
    this.statusIcons = {
      Valid: this
        .sanitizeSVG(`<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M5 13l4 4L19 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`),

      Expiring_Soon: this
        .sanitizeSVG(`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_2215_41793)">
    <path d="M6 3.5V5.89645C6 5.96275 6.02634 6.02634 6.07322 6.07322L7 7M6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11Z" stroke="#EB8700" stroke-linecap="round"/>
  </g>
  <defs>
    <clipPath id="clip0_2215_41793">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`),

      Expired: this
        .sanitizeSVG(`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_2215_41804)">
    <path d="M6 6L7.75 7.75M6 6L4.25 4.25M6 6L4.25 7.75M6 6L7.75 4.25M6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11Z" stroke="#E30613" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_2215_41804">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`),

      Pending: this
        .sanitizeSVG(`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_2215_41815)">
    <path d="M6 11.5C9.03757 11.5 11.5 9.03757 11.5 6C11.5 2.96243 9.03757 0.5 6 0.5C2.96243 0.5 0.5 2.96243 0.5 6C0.5 9.03757 2.96243 11.5 6 11.5ZM6 10.5C3.51472 10.5 1.5 8.48528 1.5 6C1.5 3.51472 3.51472 1.5 6 1.5C8.48528 1.5 10.5 3.51472 10.5 6C10.5 8.48528 8.48528 10.5 6 10.5ZM6 7C6.27614 7 6.5 6.77614 6.5 6.5V3.5C6.5 3.22386 6.27614 3 6 3C5.72386 3 5.5 3.22386 5.5 3.5V6.5C5.5 6.77614 5.72386 7 6 7ZM6 9C6.27614 9 6.5 8.77614 6.5 8.5C6.5 8.22386 6.27614 8 6 8C5.72386 8 5.5 8.22386 5.5 8.5C5.5 8.77614 5.72386 9 6 9Z" fill="#307FE1"/>
  </g>
  <defs>
    <clipPath id="clip0_2215_41815">
      <rect width="12" height="12" fill="white" transform="matrix(1 0 0 -1 0 12)"/>
    </clipPath>
  </defs>
</svg>`),

      Rejected: this
        .sanitizeSVG(`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_2215_41804)">
    <path d="M6 6L7.75 7.75M6 6L4.25 4.25M6 6L4.25 7.75M6 6L7.75 4.25M6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11Z" stroke="#E30613" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_2215_41804">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`),
    };

    this.getScrapCertificateList();
  }

  ngOnDestroy() {
    // Ensure body scroll is enabled when component is destroyed
    this.enableBodyScroll();
  }

  setupFormValidation() {
    // Angular's change detection will handle form changes automatically
    // No need for manual change detection for reactive forms
    this.certificateForm.valueChanges.subscribe(() => {
      // Form changes are automatically detected by Angular
      // Only add manual detection if you have specific performance issues
    });

    this.certificateForm.statusChanges.subscribe(() => {
      // Status changes are also automatically detected
    });
  }

  sanitizeSVG(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }
  getStatusKey(status: string): string {
    return status.replace(/\s+/g, '_'); // Replace spaces with underscores for key matching
  }

  formatDateForDisplay(
    expiryDate: string,
    uploadedOn: string,
    status: string,
  ): string {
    if (!expiryDate && !uploadedOn) return '';

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    };

    switch (status) {
      case 'Valid':
        return `Valid till ${formatDate(expiryDate)}`;
      case 'Expiring Soon':
        const today = new Date();
        const expiry = new Date(expiryDate);

        // difference in milliseconds
        const diffTime = expiry.getTime() - today.getTime();

        // convert ms → days
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return `Expiring in ${diffDays} days (${formatDate(expiryDate)})`;
      // return `Expiring in ${expiryDate} days (${formatDate(expiryDate)})`;
      case 'Expired':
        return `Expired on ${formatDate(expiryDate)}`;
      case 'Pending':
        return `Uploaded on ${formatDate(uploadedOn)}`;
      case 'Rejected':
        return `Valid till ${formatDate(expiryDate)}`;
      default:
        return formatDate(expiryDate || uploadedOn);
    }
  }

  // ----modelpopup

  modalType: 'upload' | 'view' | 'details' | 'add' | '' = '';

  selectedCertificate: any = null;

  openModal(
    cert: any = null,
    type?: 'view' | 'upload' | 'details' | 'add' | '',
  ) {
    this.selectedCertificate = cert;

    if (type) {
      this.modalType = type;
      if (type === 'add') {
        this.certificateForm.reset();
        // If navigating to add from a rejected certificate, restrict category dropdown
        if (cert && cert.status === 'Rejected' && cert.category) {
          this.filteredCategory = cert.category;
        } else if (!cert) {
          // Normal add flow: show all categories, keep any existing selection
          this.filteredCategory = null;
        }
        this.loadScrapAndUsedParts();
      }
    } else if (cert) {
      this.modalType =
        cert.status === 'Expired' || cert.status === 'Rejected'
          ? 'upload'
          : 'details';
    } else {
      this.modalType = 'upload'; // default for new upload
    }

    // Disable background scroll when modal opens
    this.disableBodyScroll();
  }

  closeModal() {
    this.modalType = '';
    this.selectedCertificate = null;
    this.isFormSubmitted = false;
    this.ImageAray = [];
    this.fileValidationErrors = [];
    this.showCategoryBottomSheet = false;

    // Enable background scroll when modal closes
    this.enableBodyScroll();
  }

  // Form validation methods
  isFieldInvalid(fieldName: string): boolean {
    const field = this.certificateForm.get(fieldName);
    const hasFieldError = !!(
      field &&
      field.invalid &&
      (field.dirty || field.touched || this.isFormSubmitted)
    );

    // Check for form-level validation errors (like date comparison)
    const hasFormError =
      this.certificateForm.errors?.['dateInvalid'] &&
      (fieldName === 'expiryDate' || fieldName === 'issueDate') &&
      (this.certificateForm.get('issueDate')?.dirty ||
        this.certificateForm.get('expiryDate')?.dirty ||
        this.isFormSubmitted);

    return hasFieldError || hasFormError;
  }

  getFieldError(fieldName: string): string {
    const field = this.certificateForm.get(fieldName);
    if (
      field &&
      field.errors &&
      (field.dirty || field.touched || this.isFormSubmitted)
    ) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors['leadingSpace']) {
        return 'Leading spaces are not allowed';
      }
      if (field.errors['pattern']) {
        return `${this.getFieldLabel(fieldName)} can only contain alphanumeric characters`;
      }
    }

    // Check for form-level date validation error
    if (
      this.certificateForm.errors?.['dateInvalid'] &&
      (fieldName === 'expiryDate' || fieldName === 'issueDate') &&
      (this.certificateForm.get('issueDate')?.dirty ||
        this.certificateForm.get('expiryDate')?.dirty ||
        this.isFormSubmitted)
    ) {
      if (fieldName === 'expiryDate') {
        return 'Expiry date must be greater than issue date';
      }
    }

    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      scrapCategory: 'Scrap Category',
      certificateName: 'Certificate Name',
      certificateNumber: 'Certificate Number',
      issuingAuthority: 'Issuing Authority',
      companyName: 'Company Name',
      issueDate: 'Issue Date',
      expiryDate: 'Expiry Date',
    };
    return labels[fieldName] || fieldName;
  }

  onSubmit() {
    this.isFormSubmitted = true;

    // Trim leading and trailing spaces for text fields before submission
    const textFields = [
      'certificateName',
      'certificateNumber',
      'issuingAuthority',
      'companyName',
    ];
    textFields.forEach((fieldName) => {
      const control = this.certificateForm.get(fieldName);
      if (control && typeof control.value === 'string') {
        const trimmed = control.value.trim();
        if (control.value !== trimmed) {
          control.setValue(trimmed);
        }
      }
    });

    // Mark all controls as touched and update validity to trigger validation messages
    Object.keys(this.certificateForm.controls).forEach((key) => {
      const control = this.certificateForm.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    // Prevent submission if form is invalid
    if (this.certificateForm.invalid) {
      return;
    }

    // Check if files are uploaded
    if (this.ImageAray.length === 0) {
      alert('Please upload at least one certificate file');
      return;
    }

    if (this.certificateForm.valid) {
      // Form is valid, proceed with submission

      this.loaderService.show();
      try {
        // Create FormData for form fields
        const formData = new FormData();

        // Add form fields to FormData
        formData.append(
          'scrap_category',
          this.certificateForm.get('scrapCategory')?.value || '',
        );
        formData.append(
          'cert_name',
          this.certificateForm.get('certificateName')?.value || '',
        );
        formData.append(
          'cert_no',
          this.certificateForm.get('certificateNumber')?.value || '',
        );
        formData.append(
          'issuing_authority',
          this.certificateForm.get('issuingAuthority')?.value || '',
        );
        formData.append(
          'company_name',
          this.certificateForm.get('companyName')?.value || '',
        );
        formData.append(
          'issue_date',
          this.certificateForm.get('issueDate')?.value || '',
        );
        formData.append(
          'expiry_date',
          this.certificateForm.get('expiryDate')?.value || '',
        );

        this.ImageAray.forEach((fileData, index) => {
          const file = this.toCertificateFile(fileData);
          formData.append(`certificate_${index}`, file, file.name);
        });

        this.apiService.add_scrap_certificate(formData).subscribe({
          next: (value) => {
            this.loaderService.hide();
            if (value.success) {
              this.certificateForm.reset();
              // alert('Certificate submitted successfully!');
              this.getScrapCertificateList();
              this.closeModal();
            } else {
              console.warn('Certificate submission failed:', value.message);
              // alert('Failed to submit certificate. Please try again.');
            }
          },
          error: (err) => {
            this.loaderService.hide();
            console.error('Error submitting certificate:', err);
            // alert('Error submitting certificate. Please try again.');
          },
        });
      } catch (error) {
        this.loaderService.hide();
        console.error('Error in certificate submission:', error);
        // alert('Error submitting certificate. Please try again.');
      }
    } else {
      // Form is invalid, show validation errors
      // alert('Please fill in all required fields');
    }
  }

  /** Send a named File so the API does not receive a filename of "blob". */
  private toCertificateFile(fileData: any): File {
    if (fileData instanceof File) {
      return fileData;
    }
    if (fileData?.file instanceof File) {
      return fileData.file;
    }

    const dataURL = typeof fileData === 'string' ? fileData : fileData?.data;
    const mime =
      fileData?.type || this.mimeFromDataUrl(dataURL) || 'application/octet-stream';
    const name = fileData?.name || this.defaultFileName(mime);
    return new File([this.dataUrlToBytes(dataURL)], name, { type: mime });
  }

  private mimeFromDataUrl(dataURL: string): string {
    return dataURL?.match(/^data:(.*?);/)?.[1] || '';
  }

  private defaultFileName(mime: string): string {
    const extensions: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'application/pdf': 'pdf',
    };
    return `certificate.${extensions[mime] || 'bin'}`;
  }

  private dataUrlToBytes(dataURL: string): ArrayBuffer {
    const base64 = dataURL.includes(',') ? dataURL.split(',')[1] : dataURL;
    const binary = atob(base64);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return buffer;
  }

  downloadInvoice(cert: any): void {
    const invoiceUrl = cert?.download_pdf;

    if (!invoiceUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = invoiceUrl;
    link.target = '_blank'; // Optional: open in new tab
    link.download = ''; // Let browser infer filename or leave blank to auto-handle
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Method to check if attachment is PDF
  isAttachmentPDF(attachment: string): boolean {
    if (!attachment) return false;
    return (
      attachment.toLowerCase().includes('.pdf') ||
      attachment.startsWith('data:application/pdf')
    );
  }

  // Method to view individual attachment - now downloads all attachments
  viewAttachment(attachment: string): void {
    // Download all attachments when any attachment is clicked
    this.downloadAllAttachments(this.selectedCertificate);
  }

  // Method to download all attachments
  downloadAllAttachments(cert: any): void {
    if (!cert?.image_path || cert.image_path.length === 0) {
      alert('No attachments to download');
      return;
    }

    cert.image_path.forEach((attachment: string, index: number) => {
      this.downloadSingleFile(attachment, cert, index);
    });
  }

  // Helper method to download a single file
  private downloadSingleFile(url: string, cert: any, index: number): void {
    // Determine file extension
    const isPDF = this.isAttachmentPDF(url);
    const extension = isPDF ? 'pdf' : 'jpg';
    const fileName = `${cert.certificate_name || 'certificate'}_${index + 1}.${extension}`;

    // Check if it's a data URL or regular URL
    if (url.startsWith('data:')) {
      // Handle data URLs (base64)
      this.downloadDataURL(url, fileName);
    } else {
      // Handle regular URLs
      this.downloadFromURL(url, fileName);
    }
  }

  // Download from data URL
  private downloadDataURL(dataURL: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Download from regular URL with fetch
  private downloadFromURL(url: string, fileName: string): void {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then((blob) => {
        const blobURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobURL);
      })
      .catch((error) => {
        console.error('Download failed:', error);
        // Fallback to opening in new tab if download fails
        window.open(url, '_blank');
      });
  }

  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.fileValidationErrors = [];
      const file = input.files[0];
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        this.fileValidationErrors.push(validation.error!);
        this.cdr.detectChanges();
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        // Store file data with type information for proper display
        const fileData = {
          data: reader.result as string,
          type: file.type,
          name: file.name,
          file,
        };
        this.ImageAray.push(fileData);
        this.cdr.detectChanges();
      };
      reader.onerror = () => {
        this.fileValidationErrors.push(`Error reading file: ${file.name}`);
      };
      reader.readAsDataURL(file);
    }
    this.cdr.detectChanges();
  }

  removeImage(file: any): void {
    const index = this.ImageAray.indexOf(file);
    if (index !== -1) {
      this.ImageAray.splice(index, 1);
    }
    this.cdr.detectChanges();
  }

  // Helper method to check if file is PDF
  isPDF(file: any): boolean {
    if (typeof file === 'string') {
      return file.startsWith('data:application/pdf');
    }
    return file?.type === 'application/pdf';
  }

  // Helper method to get file source for display
  getFileSource(file: any): string {
    if (typeof file === 'string') {
      return file;
    }
    return file?.data || file;
  }

  // Helper method to get file name
  getFileName(file: any): string {
    if (typeof file === 'string') {
      return 'PDF File';
    }
    return file?.name || 'PDF File';
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.fileValidationErrors = [];
      const file = input.files[0];
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        this.fileValidationErrors.push(validation.error!);
        this.cdr.detectChanges();
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        // Store file data with type information for proper display
        const fileData = {
          data: reader.result as string,
          type: file.type,
          name: file.name,
          file,
        };
        this.ImageAray.push(fileData);
        this.cdr.detectChanges(); // Add change detection for immediate UI update
      };
      reader.onerror = () => {
        this.fileValidationErrors.push(`Error reading file: ${file.name}`);
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    }
    this.cdr.detectChanges();
  }

  loadScrapAndUsedParts() {
    this.loaderService.show();
    try {
      const payload = {}; // Provide appropriate payload as required by the API

      this.apiService.get_category(payload).subscribe({
        next: (value) => {
          if (value.success) {
            this.scrapList = value.data.scrap_list;

            // If a category filter is set (coming from a rejected certificate),
            // ensure the form control value exactly matches one of the option values
            if (this.filteredCategory) {
              const target = (this.filteredCategory || '').trim().toLowerCase();
              const matchedCategory = this.scrapList.find(
                (category: any) =>
                  (category?.name || '').trim().toLowerCase() === target,
              );

              if (matchedCategory?.name) {
                this.certificateForm
                  .get('scrapCategory')
                  ?.setValue(matchedCategory.name);
              }
            }
          } else {
            console.warn('Customer feedback API returned success: false');
            this.scrapList = [];
          }
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error loading category list:', err);
          this.scrapList = [];
          this.loaderService.hide();
        },
      });
    } catch (error) {
      console.error('Error in category list:', error);
      this.scrapList = [];
      this.loaderService.hide();
    }
  }

  getScrapCertificateList() {
    this.loaderService.show();
    try {
      const payload = {
        action_type: '',
        user_id: this.userData?.id,
      };

      this.apiService.getScrapCertificateList(payload).subscribe({
        next: (value) => {
          if (value.success) {
            this.listdata = value?.data?.scrap_certificate_list;
            // Map API response to certificates array for template binding
            this.certificates = this.listdata.map((cert: any) => ({
              id: cert.id,
              certificate_name: cert?.certificate_name,
              certificate_no: cert?.certificate_no,
              issuing_authority: cert?.issuing_authority,
              company_name: cert?.company_name,
              issue_date: cert?.issue_date,
              expiry_date: cert?.expiry_date,
              category: cert?.category,
              image_path: cert?.image_path,
              status: cert?.status,
              created_dt: cert?.created_dt,
              uploaded_on: cert?.uploaded_on,
              // Format date for display based on status
              date: this.formatDateForDisplay(
                cert?.expiry_date,
                cert?.uploaded_on,
                cert?.status,
              ),
            }));
            this.loaderService.hide();
          } else {
            console.warn('Scrap certificate API returned success: false');
            this.listdata = [];
            this.certificates = [];
            this.loaderService.hide();
          }
        },
        error: (err) => {
          console.error('Error loading scrap certificate list:', err);
          this.listdata = [];
          this.certificates = [];
          this.loaderService.hide();
        },
      });
    } catch (error) {
      console.error('Error in scrap certificate list:', error);
      this.listdata = [];
      this.certificates = [];
      this.loaderService.hide();
    }
  }

  preventLeadingSpace(event: KeyboardEvent, input: any): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const key = event.key;
    if (NAVIGATION_KEYS.includes(key)) {
      return;
    }

    // Prevent space if it's the first character or field is empty
    if (key === ' ' && (!input?.value || input?.selectionStart === 0)) {
      event.preventDefault();
      return;
    }

    // Allow only alphanumeric characters (A-Z, a-z, 0-9) and spaces
    const allowedRegex = /^[a-zA-Z0-9 ]$/;
    if (!allowedRegex.test(key)) {
      event.preventDefault();
    }
  }

  preventLeading(event: KeyboardEvent, input: any): void {
    this.preventLeadingSpace(event, input);
  }

  handlePaste(event: ClipboardEvent, fieldName: string): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    // Strip all special characters, keeping only alphanumeric and spaces
    const sanitized = clipboardData.replace(/[^a-zA-Z0-9 ]/g, '');

    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = input.value || '';

    // Insert sanitized text at cursor position
    let newValue =
      currentValue.substring(0, start) +
      sanitized +
      currentValue.substring(end);

    // Prevent/trim leading space
    newValue = newValue.trimStart();

    input.value = newValue;
    const newCursorPos = Math.min(start + sanitized.length, newValue.length);
    input.setSelectionRange(newCursorPos, newCursorPos);

    const control = this.certificateForm.get(fieldName);
    if (control) {
      control.setValue(newValue);
      control.markAsDirty();
      control.updateValueAndValidity();
    }
  }

  handleInput(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    let value = input.value || '';
    const hasSpecialChars = /[^a-zA-Z0-9 ]/.test(value);
    const hasLeadingSpace = value.startsWith(' ');

    if (hasSpecialChars || hasLeadingSpace) {
      // Remove special characters and trim leading space
      value = value.replace(/[^a-zA-Z0-9 ]/g, '').trimStart();
      input.value = value;
      const control = this.certificateForm.get(fieldName);
      if (control) {
        control.setValue(value);
        control.markAsDirty();
        control.updateValueAndValidity();
      }
    } else {
      const control = this.certificateForm.get(fieldName);
      if (control && control.value !== value) {
        control.setValue(value);
      }
    }
  }

  handleBlur(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const trimmedValue = (input.value || '').trim();
    if (input.value !== trimmedValue) {
      input.value = trimmedValue;
      const control = this.certificateForm.get(fieldName);
      if (control) {
        control.setValue(trimmedValue);
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    } else {
      const control = this.certificateForm.get(fieldName);
      if (control) {
        control.markAsTouched();
      }
    }
  }

  trimLeadingSpace(event: Event, fieldName: string): void {
    if (event.type === 'blur') {
      this.handleBlur(event, fieldName);
    } else {
      this.handleInput(event, fieldName);
    }
  }

  /**
   * Disable body scroll when popup is open
   */
  private disableBodyScroll(): void {
    if (typeof document !== 'undefined') {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Disable scroll on body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Store scroll position for restoration
      document.body.setAttribute('data-scroll-y', scrollY.toString());
    }
  }

  /**
   * Enable body scroll when popup is closed
   */
  private enableBodyScroll(): void {
    if (typeof document !== 'undefined') {
      // Restore scroll position
      const scrollY = document.body.getAttribute('data-scroll-y');
      if (scrollY) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.removeAttribute('data-scroll-y');

        // Restore scroll position
        window.scrollTo(0, parseInt(scrollY, 10));
      } else {
        // Fallback: just restore overflow
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
    }
  }

  // alphaNumberOnly(event: any) {
  //   var inp = String.fromCharCode(event.keyCode);

  //   const data = /[0-9 ]/;

  //   if (data.test(inp)) {
  //     return true;
  //   } else {
  //     event.preventDefault();
  //     return false;
  //   }
  // }

  // Getter to provide category options for the dropdown
  get scrapCategoryOptions(): any[] {
    if (!this.scrapList || !Array.isArray(this.scrapList)) {
      return [];
    }

    if (this.filteredCategory) {
      return this.scrapList.filter(
        (category: any) => category?.name === this.filteredCategory,
      );
    }

    return this.scrapList;
  }
}
